// src/features/upload/UploadEngineProvider.tsx

import { useEffect, useMemo } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { UploadEngineContext, type UploadEngineValue } from './UploadEngineContext';
import { useUploadStore } from './uploadStore';
import { useUppy } from './useUppy';
import { useUploadCredentials } from './useUploadCredentials';
import { useIngestcfgs } from './useIngestcfgs';
import { useJobPolling } from './useJobPolling';

/**
 * Owns the upload machinery exactly once for the whole app: the Uppy
 * instance, S3 credentials, ingest profiles, job polling, and the
 * beforeunload guard. Mounted at the AppShell root — it stays alive across
 * all child navigation, so uploads survive moving between /upload, other
 * routes, and drawer open/close. UploadDrawer and UploadPage are pure
 * consumers via useUploadEngine(). Never mount these hooks anywhere else:
 * a second mount would create a second Uppy instance and duplicate job
 * polling/notifications.
 */
export function UploadEngineProvider({ children }: { children: React.ReactNode }) {
  const { authData, selectedCId, customerInfo } = useAuth();
  const items = useUploadStore((s) => s.items);

  const canIngest = Boolean(authData?.cpuser.permissions.ingest);
  const isAccountClosed = customerInfo?.customer.cfg.customerStateType === 'inactive';
  const effectiveCId = canIngest && !isAccountClosed ? selectedCId : null;

  const {
    ingestcfgs,
    selectedIngestcfgId,
    setSelectedIngestcfgId,
    hasMultiple,
    isRestricted,
  } = useIngestcfgs(effectiveCId);

  useUploadCredentials(effectiveCId);
  useJobPolling(effectiveCId);

  const { addFilesWithMeta, cancelUpload, cancelAll, retryUpload, retryAllFailed, clearCompleted } =
    useUppy(effectiveCId, ingestcfgs, selectedIngestcfgId);

  const inFlightCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'creating' || i.status === 'uploading'
  ).length;

  // beforeunload guard — only while the client is still sending bytes;
  // once items reach server-side processing, closing the tab cancels nothing.
  useEffect(() => {
    if (!canIngest || inFlightCount === 0) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [canIngest, inFlightCount > 0]);

  const value = useMemo<UploadEngineValue>(
    () => ({
      canIngest,
      effectiveCId,
      ingestcfgs,
      selectedIngestcfgId,
      setSelectedIngestcfgId,
      hasMultiple,
      isRestricted,
      addFilesWithMeta,
      cancelUpload,
      retryUpload,
      retryAllFailed,
      cancelAll,
      clearCompleted,
    }),
    [
      canIngest,
      effectiveCId,
      ingestcfgs,
      selectedIngestcfgId,
      setSelectedIngestcfgId,
      hasMultiple,
      isRestricted,
      addFilesWithMeta,
      cancelUpload,
      retryUpload,
      retryAllFailed,
      cancelAll,
      clearCompleted,
    ]
  );

  return <UploadEngineContext.Provider value={value}>{children}</UploadEngineContext.Provider>;
}
